import { Tooltip, useMediaQuery, useTheme } from '@material-ui/core'
import React, { useState } from 'react'
import {
  Datagrid,
  Filter,
  FunctionField,
  ListProps,
  Pagination,
  TextField,
  TextInput,
  sanitizeFetchType,
  useQuery,
  useTranslate,
} from 'react-admin'
import ErrorIcon from '@mui/icons-material/Error'
import { ResourceName } from '../../../core/ResourceName'
import FilterProps from '../../common/FilterProps'
import { Authority } from '../../../core/auth/Authority'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import {
  CheckIfAnyModuleHasError,
  CheckLastRefreshError,
  DeviceStatusesPreview,
} from '../common/modules/ModuleStatus'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import List from '../../common/customized-ra-components/List'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { OfflineServerDto } from '../../../core/dto/device/offline-servers/offline-server.dto'
import { isDifferentVersion } from '../../../core/common/visual.utils'
import { useColorPalette } from '../../../core/devices/devices.utils'
import { DeviceStatusCheck } from '../../../core/enum/DeviceStatusCheck'

const OfflineServerFilters = (props: FilterProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()

  return (
    <Filter {...props}>
      <TextInput source="name" alwaysOn autoFocus />
      {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
        <FilteredReferenceInput
          label="resources.offline-servers.fields.categoryId"
          source="categoryIdWithDescendants"
          reference={ResourceName.DEVICE_CATEGORIES}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'hierarchyString',
            showFilter: true,
          }}
        />
      )}
    </Filter>
  )
}

const OfflineServerGridExpand = ({ ...props }) => {
  const { record, serverDateTime } = props
  return (
    <DeviceStatusesPreview
      jsonStatus={record.jsonStatus}
      deviceType={DeviceTypes.OFFLINE_SERVER}
      referenceDateTime={serverDateTime}
    />
  )
}

export const OfflineServerList = ({ ...props }: ListProps) => {
  const theme = useTheme()
  const translate = useTranslate()
  const colorPalette = useColorPalette(theme)
  const [versionAPI, setVersionAPI] = useState<undefined | string>(undefined)

  const { data: serverDate } = useQuery({
    type: sanitizeFetchType('getServerDateTime'),
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })

  useQuery(
    {
      type: sanitizeFetchType('getAPIVersion'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: !versionAPI,
      onSuccess: (data) =>
        data?.data?.version && setVersionAPI(data.data.version),
    },
  )

  const postRowStyle = (record: any) => {
    if (!serverDate) return {}

    const detectorError = CheckLastRefreshError(
      record?.lastRefresh,
      serverDate?.serverDateTime,
    )
    if (detectorError) return colorPalette(DeviceStatusCheck.ERROR)

    const detectorWarning = CheckIfAnyModuleHasError(
      record.jsonStatus,
      DeviceTypes.OFFLINE_SERVER,
      serverDate?.serverDateTime,
    )
    if (detectorWarning) return colorPalette(DeviceStatusCheck.WARNING)
    return colorPalette(DeviceStatusCheck.SUCCESS)
  }

  const extractVersion = (jsonStatus: string | null | undefined) => {
    try {
      const arr =
        typeof jsonStatus === 'string' ? JSON.parse(jsonStatus) : jsonStatus
      if (Array.isArray(arr) && arr.length > 0) {
        return arr[0]?.identity?.version?.version || ''
      }
      return ''
    } catch {
      return ''
    }
  }

  return (
    <List
      {...props}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      exporter={false}
      sort={{
        field: 'id',
        order: 'DESC',
      }}
      filters={<OfflineServerFilters />}
      bulkActionButtons={false}
    >
      <Datagrid
        size="small"
        rowClick={(id) => `${ResourceName.OFFLINE_SERVERS}/${id}/show`}
        hasBulkActions={false}
        rowStyle={postRowStyle}
        isRowExpandable={(record) =>
          record?.jsonStatus !== undefined && record?.jsonStatus !== null
        }
        expand={
          <OfflineServerGridExpand
            serverDateTime={serverDate?.serverDateTime}
          />
        }
      >
        <TextField source="id" label="ID" />
        <TextField source="name" />
        <FunctionField<OfflineServerDto>
          render={(record?: OfflineServerDto) =>
            record?.jsonStatus &&
            versionAPI &&
            isDifferentVersion(
              versionAPI,
              extractVersion(record.jsonStatus),
            ) ? (
              <Tooltip
                title={translate('resources.offline-servers.wrongVersion')}
                enterTouchDelay={0}
              >
                <ErrorIcon />
              </Tooltip>
            ) : undefined
          }
          onClick={(e) => e.stopPropagation()}
        />
        <TextField source="address" />
      </Datagrid>
    </List>
  )
}
